import React, { useEffect, useRef } from 'react';
import { ColorType, LineStyle, createChart } from 'lightweight-charts';

function toBusinessDay(value) {
  const text = String(value || '');
  const [year, month, day] = text.split('-').map((part) => Number(part));
  if (!year || !month || !day) return null;
  return { year, month, day };
}

function setSeriesData(series, rows, key) {
  return setSeriesDataWithMode(series, rows, key, true);
}

function setSeriesDataWithMode(series, rows, key, valuesAreIndexed = true) {
  const data = rows
    .map((row) => {
      const time = toBusinessDay(row?.date);
      const rawValue = row?.[key];
      if (rawValue === null || rawValue === undefined || rawValue === '') return null;
      const numeric = Number(rawValue);
      const value = valuesAreIndexed ? numeric - 100 : numeric;
      if (!time || !Number.isFinite(value)) return null;
      return { time, value };
    })
    .filter(Boolean);

  series.setData(data);
  return data;
}

function buildSpreadScale(values = []) {
  const clean = values.filter((value) => Number.isFinite(value));
  if (!clean.length) return null;
  const rawMin = Math.min(...clean);
  const rawMax = Math.max(...clean);
  const extent = Math.max(Math.abs(rawMin), Math.abs(rawMax), 0.5);
  const pad = Math.max(extent * 0.14, 0.35);
  return {
    minValue: -extent - pad,
    maxValue: extent + pad
  };
}

export function BasketBehaviorChart({
  data = [],
  height = 156,
  colors = {},
  lineColor = '#bc4b1f',
  longColor = '#1c6b45',
  shortColor = '#184b73',
  gridColor = '#d9cfc0',
  textColor = '#5c554d',
  backgroundColor = '#ffffff',
  showAuxiliary = true,
  valuesAreIndexed = true,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const spreadSeriesRef = useRef(null);
  const longSeriesRef = useRef(null);
  const shortSeriesRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || chartRef.current) return undefined;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth || 560,
      height,
      layout: {
        background: { type: ColorType.Solid, color: backgroundColor },
        textColor,
        fontFamily: '"Courier New", monospace',
        fontSize: 11,
      },
      grid: {
        vertLines: { visible: false, color: gridColor },
        horzLines: { color: gridColor, style: LineStyle.Dashed },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      leftPriceScale: {
        visible: false,
        autoScale: true,
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true,
      },
      crosshair: {
        vertLine: { color: gridColor, width: 1, style: LineStyle.Solid, labelVisible: true },
        horzLine: { color: gridColor, width: 1, style: LineStyle.Solid, labelVisible: true },
      },
      localization: {
        priceFormatter: (value) => {
          const numeric = Number(value);
          const sign = numeric > 0 ? '+' : '';
          return `${sign}${numeric.toFixed(1)}%`;
        },
      },
    });

    const spreadSeries = chart.addLineSeries({
      color: lineColor,
      lineWidth: 3,
      lastValueVisible: true,
      priceLineVisible: false,
      priceScaleId: 'right',
    });
    spreadSeries.createPriceLine({
      price: 0,
      color: gridColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: 'Flat',
    });

    const longSeries = chart.addLineSeries({
      color: longColor,
      lineWidth: 2,
      lineStyle: LineStyle.Dashed,
      lastValueVisible: false,
      priceLineVisible: false,
      priceScaleId: 'left',
    });

    const shortSeries = chart.addLineSeries({
      color: shortColor,
      lineWidth: 2,
      lineStyle: LineStyle.Dashed,
      lastValueVisible: false,
      priceLineVisible: false,
      priceScaleId: 'left',
    });

    chartRef.current = chart;
    spreadSeriesRef.current = spreadSeries;
    longSeriesRef.current = longSeries;
    shortSeriesRef.current = shortSeries;

    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: containerRef.current.clientWidth,
        height,
      });
    });
    resizeObserver.observe(containerRef.current);
    resizeObserverRef.current = resizeObserver;

    return () => {
      resizeObserver.disconnect();
      resizeObserverRef.current = null;
      chart.remove();
      chartRef.current = null;
      spreadSeriesRef.current = null;
      longSeriesRef.current = null;
      shortSeriesRef.current = null;
    };
  }, [backgroundColor, gridColor, height, lineColor, longColor, shortColor, textColor]);

  useEffect(() => {
    if (!chartRef.current || !spreadSeriesRef.current || !longSeriesRef.current || !shortSeriesRef.current) return;

    const spreadData = setSeriesDataWithMode(spreadSeriesRef.current, data, 'spread', valuesAreIndexed);
    const spreadScale = buildSpreadScale(spreadData.map((row) => row.value));
    spreadSeriesRef.current.applyOptions({
      autoscaleInfoProvider: spreadScale
        ? () => ({
            priceRange: {
              minValue: spreadScale.minValue,
              maxValue: spreadScale.maxValue,
            },
          })
        : undefined,
    });
    if (showAuxiliary) {
      setSeriesDataWithMode(longSeriesRef.current, data, 'long', valuesAreIndexed);
      setSeriesDataWithMode(shortSeriesRef.current, data, 'short', valuesAreIndexed);
    } else {
      longSeriesRef.current.setData([]);
      shortSeriesRef.current.setData([]);
    }

    if (spreadData.length > 1) {
      chartRef.current.timeScale().fitContent();
    }
  }, [data, showAuxiliary, valuesAreIndexed]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: `${height}px`,
      }}
      aria-label="Basket performance chart"
    />
  );
}

export default BasketBehaviorChart;
